import { useCallback, useMemo, useEffect, useState } from 'react';

const GRID_SIZE = 20;
const CONTAINER_WIDTH = 2000;
const CONTAINER_HEIGHT = 2000;

/**
 * Custom hook for grid and connection calculations
 */
export const useGridCalculations = (flowItems, showExpansionLines = true) => {
  const [domPositionsReady, setDomPositionsReady] = useState(false);
  const [componentPositions] = useState(() => new Map()); // Store positions by component ID + panel ID
  
  // Generate grid dots based on container size
  const generateGridDots = useCallback(() => {
    const dots = [];
    
    // Calculate bounds based on panel positions
    let minX = 0;
    let minY = 0;
    let maxX = CONTAINER_WIDTH;
    let maxY = CONTAINER_HEIGHT;
    
    if (flowItems.length > 0) {
      const panels = flowItems.filter(item => item.type === 'panel');
      const plusSigns = flowItems.filter(item => item.type === 'plus');
      
      if (panels.length > 0 || plusSigns.length > 0) {
        // Include both panels and plus signs in bounds calculation
        const allItems = [...panels, ...plusSigns];
        minX = Math.min(...allItems.map(item => item.x));
        minY = Math.min(...allItems.map(item => item.y));
        maxX = Math.max(...allItems.map(item => item.type === 'panel' ? item.x + item.width : item.x + 32));
        maxY = Math.max(...allItems.map(item => item.type === 'panel' ? item.y + item.height : item.y + 32));
        
        // Add padding around the items
        const padding = 200;
        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = maxX + padding;
        maxY = maxY + padding;
      }
    }
    
    const gridWidth = Math.max(CONTAINER_WIDTH, Math.max(0, maxX - minX));
    const gridHeight = Math.max(CONTAINER_HEIGHT, Math.max(0, maxY - minY));
    
    /* const dotsX = Math.floor(gridWidth / GRID_SIZE);
    const dotsY = Math.floor(gridHeight / GRID_SIZE);
    
    for (let i = 0; i <= dotsX; i++) {
      for (let j = 0; j <= dotsY; j++) {
        dots.push({
          key: `${i}-${j}`,
          x: i * GRID_SIZE,
          y: j * GRID_SIZE,
        });
      }
    } */
    
    return { dots, width: gridWidth, height: gridHeight };
  }, [flowItems]);

  // Generate connection lines between panels and plus signs
  const generateConnections = useCallback(() => {
    const connections = [];
    
    const panels = flowItems
      .filter((item) => item.type === "panel")
      .sort((a, b) => a.x - b.x);
    
    panels.forEach((panel, index) => {
      const nextPanel = panels[index + 1];
      
      if (nextPanel) {
        const startX = panel.x + panel.width;
        const endX = nextPanel.x;
        
        // Use a fixed Y position for main step connections (middle of typical panel height)
        const fixedY = panel.y + 80; // Fixed vertical position
        
        // Only add connection if distance > threshold
        const distance = Math.abs(endX - startX);
        if (distance > 50) {
          // Simple horizontal line
          connections.push({
            key: `conn-${panel.id}-${nextPanel.id}`,
            x1: startX,
            y1: fixedY,
            x2: endX,
            y2: fixedY,
            isJustDot: false
          });
          
          // Add connection dots
          connections.push({
            key: `dot-start-${panel.id}`,
            x: startX,
            y: fixedY,
            isJustDot: true
          });
          
          connections.push({
            key: `dot-end-${nextPanel.id}`,
            x: endX,
            y: fixedY,
            isJustDot: true
          });
        }
      }
    });
    
    // Add expansion lines if enabled
    if (showExpansionLines) {
      // Group panels by component ID to find components that span multiple steps
      const componentPanelGroups = new Map();
      
      panels.forEach((panel) => {
        if (panel.components) {
          panel.components.forEach(component => {
            const compId = component.id;
            if (!componentPanelGroups.has(compId)) {
              componentPanelGroups.set(compId, []);
            }
            componentPanelGroups.get(compId).push({
              panel,
              component,
              isExpandedCopy: component.isExpandedCopy
            });
          });
        }
      });
      
      // For each component that appears in multiple panels, draw connector lines
      componentPanelGroups.forEach((instances, componentId) => {
        if (instances.length > 1) {
          // Sort instances by panel x position
          instances.sort((a, b) => a.panel.x - b.panel.x);
          
          // Draw lines between consecutive instances
          for (let i = 0; i < instances.length - 1; i++) {
            const startInstance = instances[i];
            const endInstance = instances[i + 1];
            
            // Calculate Y position based on component position within panel
            const startComponentIndex = startInstance.panel.components.indexOf(startInstance.component);
            const endComponentIndex = endInstance.panel.components.indexOf(endInstance.component);
            
            // Try to use DOM positions if available
            const startPositionKey = `${startInstance.component.id}-${startInstance.panel.id}`;
            const endPositionKey = `${endInstance.component.id}-${endInstance.panel.id}`;
            
            let startY = componentPositions.get(startPositionKey);
            let endY = componentPositions.get(endPositionKey);
            
            // Add fixed offset to nudge lines down to align with visual center
            const yOffset = 0;
            
            // Fallback to calculation if DOM positions not yet available
            if (startY === undefined || endY === undefined) {
              const headerAndImageHeight = 120;
              const startAvailableHeight = startInstance.panel.height - headerAndImageHeight;
              const endAvailableHeight = endInstance.panel.height - headerAndImageHeight;
              const startCompCount = startInstance.panel.components.length;
              const endCompCount = endInstance.panel.components.length;
              const gap = 8;
              const startCompHeight = (startAvailableHeight - (gap * (startCompCount - 1))) / startCompCount;
              const endCompHeight = (endAvailableHeight - (gap * (endCompCount - 1))) / endCompCount;
              
              startY = startInstance.panel.y + headerAndImageHeight + (startComponentIndex * (startCompHeight + gap)) + (startCompHeight / 2);
              endY = endInstance.panel.y + headerAndImageHeight + (endComponentIndex * (endCompHeight + gap)) + (endCompHeight / 2);
            } else {
              // Apply offset to DOM-measured positions
              startY += yOffset;
              endY += yOffset;
            }
            
            // X position: from right edge of start panel to left edge of end panel
            const startX = startInstance.panel.x + startInstance.panel.width;
            const endX = endInstance.panel.x;
            const midX = (startX + endX) / 2;
            
            // Draw stepped line connection (like main step connections)
            // 1. Horizontal line from component to midpoint
            connections.push({
              key: `expansion-line-${componentId}-${i}-h1`,
              x1: startX,
              y1: startY,
              x2: midX,
              y2: startY,
              isExpansionLine: true
            });
            
            // 2. Vertical line connecting the two heights
            connections.push({
              key: `expansion-line-${componentId}-${i}-v`,
              x1: midX,
              y1: startY,
              x2: midX,
              y2: endY,
              isExpansionLine: true
            });
            
            // 3. Horizontal line from midpoint to next component
            connections.push({
              key: `expansion-line-${componentId}-${i}-h2`,
              x1: midX,
              y1: endY,
              x2: endX,
              y2: endY,
              isExpansionLine: true
            });
          }
        }
      });
    }
    
    return connections;
  }, [flowItems, showExpansionLines]);

  const { dots: gridDots, width: safeGridWidth, height: safeGridHeight } = useMemo(
    () => generateGridDots(),
    [generateGridDots]
  );

  // Update expansion line positions from DOM after render
  useEffect(() => {
    if (!showExpansionLines) return;
    
    // Wait for next frame to ensure DOM is updated
    requestAnimationFrame(() => {
      const panels = flowItems.filter(item => item.type === "panel");
      let updated = false;
      
      panels.forEach((panel) => {
        const panelEl = document.querySelector(`[data-panel-id="${panel.id}"]`);
        if (panelEl && panel.components) {
          panel.components.forEach((component) => {
            // Query component by its unique ID instead of index
            const componentEl = panelEl.querySelector(`[data-component-id="${component.id}"]`);
            if (componentEl) {
              // Get component position in flow container coordinates
              const rect = componentEl.getBoundingClientRect();
              const flowContainer = document.querySelector('.flow-container');
              if (flowContainer) {
                const containerRect = flowContainer.getBoundingClientRect();
                // Calculate Y position in the same coordinate system as panel.y
                const relativeY = rect.top - containerRect.top + flowContainer.scrollTop;
                const centerY = relativeY + rect.height / 2;
                
                // Store position using component ID + panel ID as key
                const positionKey = `${component.id}-${panel.id}`;
                const existingY = componentPositions.get(positionKey);
                
                if (existingY !== centerY) {
                  componentPositions.set(positionKey, centerY);
                  updated = true;
                }
              }
            }
          });
        }
      });
      
      // Trigger re-render if positions were updated
      if (updated) {
        setDomPositionsReady(prev => !prev);
      }
    });
  }, [flowItems, showExpansionLines, componentPositions]);

  const connections = useMemo(
    () => generateConnections(),
    [generateConnections, domPositionsReady] // Re-calculate when DOM positions are ready
  );

  return {
    gridDots,
    safeGridWidth,
    safeGridHeight,
    connections,
    GRID_SIZE,
    CONTAINER_WIDTH,
    CONTAINER_HEIGHT
  };
};
